import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [] });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get('/cart');
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const addToCart = useCallback(async (productId, quantity = 1, variation = null) => {
    if (!isAuthenticated) {
      throw new Error('Please login to add items to cart');
    }
    
    try {
      await api.post('/cart/add', { product_id: productId, quantity, variation });
      await fetchCart();
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to add to cart');
    }
  }, [isAuthenticated, fetchCart]);

  const removeFromCart = useCallback(async (productId) => {
    try {
      await api.post('/cart/remove', { product_id: productId });
      await fetchCart();
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to remove from cart');
    }
  }, [fetchCart]);

  const clearCart = useCallback(async () => {
    try {
      await api.post('/cart/clear', {});
      setCart({ items: [] });
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to clear cart');
    }
  }, []);

  const cartTotal = cart.items.reduce((total, item) => {
    const price = item.product?.price || 0;
    return total + (price * item.quantity);
  }, 0);

  const cartCount = cart.items.reduce((count, item) => count + item.quantity, 0);

  const value = {
    cart,
    loading,
    fetchCart,
    addToCart,
    removeFromCart,
    clearCart,
    cartTotal,
    cartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
